import { tierFromPlanPrice } from "./planPermissions";

/**
 * Basic plan (50₪ / legacy $10): catalog + WhatsApp cart. Professional+ adds form checkout & orders dashboard.
 */
export function isActiveBasicPlanFromMySubscriptionResponse(data) {
  return getPlanTierFromMySubscriptionResponse(data) === 1;
}

/**
 * Normalize the current plan name from `/subscription/my-subscription`.
 * Returns one of: free | basic | professional | advanced | wholesale | enterprise | unknown
 */
export function getPlanKeyFromMySubscriptionResponse(data) {
  const sub = data?.subscription;
  if (!sub || String(sub.status || "").toLowerCase() !== "active") return "free";
  const planName = String(sub?.plan?.name || sub?.plan || "free")
    .trim()
    .toLowerCase();
  if (!planName) return "free";

  if (planName === "basic") return "basic";
  if (planName === "professional") return "professional";
  if (planName === "advanced") return "advanced";
  if (planName === "wholesale") return "wholesale";
  if (planName === "enterprise") return "enterprise";
  if (planName === "free") return "free";

  const priceTier = tierFromPlanPrice(sub?.plan?.price);
  if (priceTier === 1) return "basic";
  if (priceTier === 2) return "professional";
  if (priceTier === 3) return "advanced";
  if (priceTier === 4) return "wholesale";

  return "unknown";
}

/** Professional (80₪ legacy) and advanced (100₪) are one merged plan. */
export function isMergedProfessionalPlan(planKey) {
  const k = String(planKey || "").trim().toLowerCase();
  return k === "professional" || k === "advanced";
}

/** Canonical family: professional + advanced → professional. */
export function planFamilyKey(planKey) {
  const k = String(planKey || "").trim().toLowerCase();
  if (isMergedProfessionalPlan(k)) return "professional";
  return k;
}

/** Higher number means more access. Merged professional = old advanced (tier 3). */
export function getPlanTier(planKey) {
  const k = String(planKey || "").toLowerCase();
  if (k === "wholesale") return 4;
  if (k === "advanced" || k === "enterprise" || k === "professional") return 3;
  if (k === "basic") return 1;
  return 0; // free/unknown
}

/**
 * Prefer `features.plan_tier` from `/subscription/my-subscription` (server-side tier).
 * Falls back to plan name parsing when features are missing (older clients).
 */
export function getPlanTierFromMySubscriptionResponse(data) {
  const ft = data?.features?.plan_tier;
  if (ft !== null && ft !== undefined && Number.isFinite(Number(ft))) {
    return Number(ft);
  }
  return getPlanTier(getPlanKeyFromMySubscriptionResponse(data));
}

/** Wholesale B2B plan (120₪): manage dealers, wholesale prices, separate orders. */
export function isWholesalePlanFromMySubscriptionResponse(data) {
  return getPlanKeyFromMySubscriptionResponse(data) === "wholesale";
}

/** Active paid/free row from `/subscription/my-subscription` (not SUBSCRIPTION_EXPIRED payload). */
export function isSubscriptionEffectivelyActiveFromResponse(data) {
  if (!data || data.expired === true || data.admin_suspended === true) return false;
  const msg = String(data.message || "");
  if (
    data.success === false &&
    (msg.includes("SUBSCRIPTION_EXPIRED") || msg.includes("SUBSCRIPTION_SUSPENDED"))
  ) {
    return false;
  }
  const sub = data.subscription;
  return Boolean(sub && String(sub.status || "").toLowerCase() === "active");
}

/** Wholesale dashboard access: plan-features and/or my-subscription features. */
export function hasWholesaleDashboardAccess(mySubData, planFeaturesData) {
  if (planFeaturesData?.wholesale === true) return true;
  if (mySubData?.features?.wholesale === true) return true;
  return isWholesalePlanFromMySubscriptionResponse(mySubData);
}

/** Cost price field + net profit reports (merged professional and wholesale). */
export function canUseCostPriceFromMySubscriptionResponse(data) {
  const ft = data?.features?.cost_price;
  if (ft === true) return true;
  if (ft === false) return false;
  return getPlanTierFromMySubscriptionResponse(data) >= 3;
}

/** Storefront cart + WhatsApp handoff (basic 50₪+). */
export function canUseStorefrontCartFromMySubscriptionResponse(data) {
  const ft = data?.features?.storefront_cart;
  if (ft === true) return true;
  if (ft === false) return false;
  return getPlanTierFromMySubscriptionResponse(data) >= 1;
}

/** Form checkout + orders dashboard (merged professional and above). */
export function canUseStorefrontOrdersFromMySubscriptionResponse(data) {
  const ft = data?.features?.storefront_orders;
  if (ft === true) return true;
  if (ft === false) return false;
  return getPlanTierFromMySubscriptionResponse(data) >= 2;
}

/** Inventory dashboard + variant stock (merged professional and above). */
export function canUseInventoryManagementFromPlanKey(planKey) {
  return getPlanTier(planKey) >= 2;
}

export function canUseInventoryManagementFromMySubscriptionResponse(data) {
  const inv = data?.features?.inventory;
  if (inv === true) return true;
  if (inv === false) return false;
  return getPlanTierFromMySubscriptionResponse(data) >= 2;
}

function planCapRaw(plan) {
  if (!plan || typeof plan !== "object") return undefined;
  if (Object.prototype.hasOwnProperty.call(plan, "max_products")) {
    return plan.max_products;
  }
  if (Object.prototype.hasOwnProperty.call(plan, "maxProducts")) {
    return plan.maxProducts;
  }
  return undefined;
}

function planNameOf(plan) {
  if (!plan || typeof plan !== "object") return "";
  return String(plan.name || plan.planName || plan.plan || "")
    .trim()
    .toLowerCase();
}

/**
 * Unlimited products: max_products === null (any paid plan).
 * Free: max_products = 0 means zero products.
 * Legacy wholesale rows stored 0 to mean unlimited.
 */
export function hasUnlimitedProducts(plan) {
  if (!plan || typeof plan !== "object") return false;
  const name = planNameOf(plan);
  if (name === "free" || name === "basic") return false;
  const max = planCapRaw(plan);
  if (max === null) return true;
  if (name === "wholesale" && (max === 0 || max === "0")) return true;
  return false;
}

/**
 * Product cap for UI/state: null = unlimited, 0 = no products (free), N = limit.
 */
export function normalizeMaxProductsFromPlan(plan) {
  if (hasUnlimitedProducts(plan)) return null;
  const max = planCapRaw(plan);
  if (max === null || max === undefined) return 0;
  const n = Number(max);
  return Number.isFinite(n) ? n : 0;
}

/** Label for subscription dashboard ("المنتجات المتاحة"). */
export function formatMaxProductsForDisplay(planOrSub, language = "ar") {
  if (hasUnlimitedProducts(planOrSub)) {
    return language === "ar" ? "غير محدود" : "Unlimited";
  }
  return String(normalizeMaxProductsFromPlan(planOrSub));
}
